<template>
  <vs-popup
    class="video-presentation-popup"
    title="Vídeo de apresentação"
    :active.sync="internalActive"
    :button-close-hidden="isSaving"
  >
    <div class="video-presentation">
      <p class="video-presentation-intro">
        Adicione um vídeo curto do YouTube se apresentando. Sugerimos publicar
        como <strong>"Não listado"</strong> para que só quem tem o link consiga ver.
      </p>

      <div class="video-presentation-instructions">
        <button
          type="button"
          class="video-presentation-instructions-toggle"
          @click="showInstructions = !showInstructions"
        >
          {{ showInstructions ? 'Esconder' : 'Como deixar meu vídeo "Não listado" no YouTube?' }}
        </button>
        <ol v-if="showInstructions" class="video-presentation-instructions-list">
          <li>Faça upload do vídeo no <a href="https://studio.youtube.com" target="_blank" rel="noopener noreferrer">YouTube Studio</a>.</li>
          <li>Em "Visibilidade", selecione <strong>Não listado</strong>.</li>
          <li>Salve e copie o link do vídeo (botão "Compartilhar").</li>
          <li>Cole o link aqui em baixo. Você pode trocar quando quiser.</li>
        </ol>
      </div>

      <div class="video-presentation-field">
        <label for="video-presentation-url">URL do vídeo no YouTube</label>
        <input
          id="video-presentation-url"
          type="url"
          v-model="urlInput"
          placeholder="https://www.youtube.com/watch?v=..."
          :disabled="isSaving"
          @input="onUrlInput"
        />
        <p v-if="error" class="video-presentation-error">{{ error }}</p>
      </div>

      <div v-if="previewEmbedUrl" class="video-presentation-preview">
        <p class="video-presentation-preview-label">Pré-visualização</p>
        <div class="video-presentation-iframe-wrapper">
          <iframe
            :src="previewEmbedUrl"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            frameborder="0"
            referrerpolicy="strict-origin-when-cross-origin"
          ></iframe>
        </div>
      </div>

      <div class="video-presentation-actions">
        <button
          type="button"
          class="video-presentation-secondary-btn"
          :disabled="isSaving"
          @click="cancel"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="video-presentation-primary-btn"
          :disabled="isSaving || !canSave"
          @click="save"
        >
          {{ isSaving ? 'Salvando...' : 'Salvar vídeo' }}
        </button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
const YOUTUBE_PATTERNS = [
  /^https?:\/\/(?:www\.)?youtube\.com\/watch\?(?:.*&)?v=([A-Za-z0-9_-]{6,})/i,
  /^https?:\/\/(?:www\.)?youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/i,
  /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/i,
  /^https?:\/\/youtu\.be\/([A-Za-z0-9_-]{6,})/i,
];

function extractYoutubeId(url) {
  const trimmed = (url || '').trim();
  if (!trimmed) return null;
  for (const pattern of YOUTUBE_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export default {
  name: 'VideoPresentationModal',
  props: {
    active: {
      type: Boolean,
      required: true,
    },
    existing: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    urlInput: '',
    showInstructions: false,
    previewEmbedUrl: null,
    error: '',
    isSaving: false,
    _debounceTimer: null,
  }),
  computed: {
    internalActive: {
      get() {
        return this.active;
      },
      set(value) {
        this.$emit('update:active', value);
      },
    },
    canSave() {
      return !!extractYoutubeId(this.urlInput);
    },
  },
  watch: {
    active(value) {
      if (value) {
        this.urlInput = (this.existing && this.existing.source_url) || '';
        this.error = '';
        this.previewEmbedUrl = this.computeEmbed(this.urlInput);
      } else {
        this.cleanupDebounce();
      }
    },
  },
  beforeDestroy() {
    this.cleanupDebounce();
  },
  methods: {
    onUrlInput() {
      this.cleanupDebounce();
      this.error = '';
      this._debounceTimer = setTimeout(() => {
        const embed = this.computeEmbed(this.urlInput);
        this.previewEmbedUrl = embed;
        if (!embed && this.urlInput.trim()) {
          this.error = 'Cole um link válido do YouTube (watch, youtu.be, shorts ou embed).';
        }
      }, 300);
    },
    cleanupDebounce() {
      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = null;
      }
    },
    computeEmbed(url) {
      const id = extractYoutubeId(url);
      return id ? `https://www.youtube.com/embed/${id}` : null;
    },
    cancel() {
      this.internalActive = false;
    },
    async save() {
      if (!this.canSave || this.isSaving) return;
      this.isSaving = true;
      this.error = '';
      try {
        await this.$store.dispatch('presentation/saveVideo', this.urlInput.trim());
        this.$vs.notify({
          title: 'Vídeo salvo!',
          text: 'Seu vídeo de apresentação foi vinculado ao perfil.',
          iconPack: 'feather',
          icon: 'icon-check-circle',
          color: 'success',
        });
        this.$emit('saved');
        this.internalActive = false;
      } catch (error) {
        const responseMessage =
          error && error.response && error.response.data && (error.response.data.message || error.response.data.errors);
        if (responseMessage && typeof responseMessage === 'object') {
          const firstKey = Object.keys(responseMessage)[0];
          this.error = (responseMessage[firstKey] && responseMessage[firstKey][0]) || 'Não foi possível salvar o vídeo.';
        } else if (typeof responseMessage === 'string') {
          this.error = responseMessage;
        } else {
          this.error = 'Não foi possível salvar o vídeo. Tente novamente.';
        }
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss">
.video-presentation-popup {
  .vs-popup {
    max-width: 600px;
    width: 100%;
    border-radius: 16px;
  }
  .vs-popup--content {
    padding: 24px;
  }
}

.video-presentation {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: Roboto, sans-serif;
  color: #333;

  &-intro {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: #4f4f4f;

    strong {
      color: #333;
    }
  }

  &-instructions {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &-toggle {
      background: transparent;
      border: none;
      color: #4169e1;
      font-family: Roboto, sans-serif;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      align-self: flex-start;
      padding: 0;

      &:hover {
        text-decoration: underline;
      }
    }

    &-list {
      margin: 0;
      padding-left: 20px;
      font-size: 14px;
      line-height: 1.5;
      color: #4f4f4f;

      a {
        color: #4169e1;
        text-decoration: underline;
      }
    }
  }

  &-field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
      font-size: 14px;
      font-weight: 500;
      color: #333;
    }

    input {
      height: 40px;
      padding: 0 12px;
      border-radius: 8px;
      border: 0.5px solid #a8a8a8;
      font-size: 14px;
      color: #333;
      font-family: Roboto, sans-serif;
      transition: border-color 0.2s ease;

      &:focus {
        outline: none;
        border-color: #4169e1;
      }

      &:disabled {
        background: #f5f5f5;
        cursor: not-allowed;
      }
    }
  }

  &-error {
    margin: 0;
    color: #de2348;
    font-size: 13px;
  }

  &-preview {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &-label {
      margin: 0;
      font-size: 13px;
      font-weight: 500;
      color: #696969;
    }
  }

  &-iframe-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    background: #000;

    iframe {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      border: 0;
    }
  }

  &-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  &-secondary-btn,
  &-primary-btn {
    height: 40px;
    padding: 0 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: filter 0.2s ease;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &-secondary-btn {
    background: #fff;
    color: #4f4f4f;
    border: 0.5px solid #a8a8a8;
  }

  &-primary-btn {
    background: #4169e1;
    color: #fff;
    border: none;

    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }
  }
}
</style>
